import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { resolveApplianceCategory } from "@/lib/applianceCategoryDisplay";

export interface NormalizedAppliance {
  id: string;
  name: string;
  modelNumber: string;
  brand: string;
  category: string;
  imageUrl?: string;
  typeShieldUrl?: string;
  productNumber?: string;
  serialNumber?: string;
  type?: string;
  dryerTechnology?: string | null;
  eprelReference?: string;
  eprelData?: unknown;
}

const isUuid = (v: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);

// One source for the appliance's real category (company category, washer dryer from the scanned type).
const categoryDisplayName = (cat: string | null | undefined, productType?: string | null) =>
  resolveApplianceCategory({ category: cat, productType }) ?? "Appliance";

const fromLocalStorage = (id: string): NormalizedAppliance | null => {
  try {
    const stored = localStorage.getItem("recentlyAccessedAppliances");
    if (!stored) return null;
    const list = JSON.parse(stored);
    const a = list.find((x: { id?: string }) => x.id === id);
    if (!a) return null;
    const brand = a.brand || (a.name ? String(a.name).split(" ")[0] : "") || "";
    return {
      id,
      name: a.name || "Unknown Appliance",
      modelNumber: a.modelNumber || "",
      brand,
      category: categoryDisplayName(a.category, a.type),
      imageUrl: a.imageUrl || undefined,
      typeShieldUrl: a.typeShieldUrl || undefined,
      productNumber: a.productNumber || undefined,
      serialNumber: a.serialNumber || undefined,
      type: a.type || undefined,
      dryerTechnology: a.dryerTechnology || undefined,
      eprelReference: a.eprelReference || undefined,
      eprelData: a.eprelData,
    };
  } catch {
    return null;
  }
};

const syncToLocalStorage = (a: NormalizedAppliance) => {
  try {
    const stored = localStorage.getItem("recentlyAccessedAppliances");
    const existing = stored ? JSON.parse(stored) : [];
    const next = {
      id: a.id,
      name: a.name,
      modelNumber: a.modelNumber,
      brand: a.brand,
      category: (a.category || "other").toLowerCase().replace(/\s+/g, "-"),
      imageUrl: a.imageUrl,
      typeShieldUrl: a.typeShieldUrl,
      productNumber: a.productNumber,
      serialNumber: a.serialNumber,
      type: a.type,
      dryerTechnology: a.dryerTechnology,
      eprelReference: a.eprelReference,
      eprelData: a.eprelData,
    };
    const updated = [
      next,
      ...existing.filter((e: { id?: string }) => e.id !== a.id),
    ].slice(0, 10);
    localStorage.setItem("recentlyAccessedAppliances", JSON.stringify(updated));
  } catch {
    /* ignore */
  }
};

/**
 * Loads the appliance for a workflow page.
 * Priority: localStorage cache (instant) → Supabase (authoritative).
 * Returns notFound=true when the id is a UUID but no DB row matches.
 */
export function useAppliance(id: string | undefined) {
  const { user } = useAuth();
  const [appliance, setAppliance] = useState<NormalizedAppliance | null>(() =>
    id ? fromLocalStorage(id) : null,
  );
  const [isLoading, setIsLoading] = useState<boolean>(() => {
    if (!id) return false;
    // If we already have it in localStorage we don't need a blocking spinner,
    // but we'll still revalidate from DB in the background.
    return !fromLocalStorage(id);
  });
  const [notFound, setNotFound] = useState(false);
  // FIX-286: bump to re-read the row after an in-place edit (e.g. Complete appliance details).
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    if (!id || !user || !isUuid(id)) {
      setIsLoading(false);
      return;
    }

    (async () => {
      // Note: no `.eq("user_id", ...)` filter — RLS already scopes appliances
      // to the active company, and any company member must be able to open
      // work orders created by teammates.
      const { data, error } = await supabase
        .from("appliances")
        .select(
          "id, name, model_number, brand, category, image_url, type_shield_url, product_number, serial_number, product_type, dryer_technology, eprel_reference, eprel_data",
        )
        .eq("id", id)
        .maybeSingle();

      if (cancelled) return;

      if (error) {
        console.error("useAppliance: failed to load appliance:", error);
        setIsLoading(false);
        return;
      }

      if (!data) {
        // Only flag notFound if we also had no localStorage hit.
        if (!fromLocalStorage(id)) setNotFound(true);
        setIsLoading(false);
        return;
      }

      const normalized: NormalizedAppliance = {
        id: data.id,
        name:
          data.name ||
          [data.brand, data.model_number].filter(Boolean).join(" ") ||
          "Unknown Appliance",
        modelNumber: data.model_number || "",
        brand: data.brand || (data.name ? String(data.name).split(" ")[0] : ""),
        category: categoryDisplayName(data.category, data.product_type),
        imageUrl: data.image_url || undefined,
        typeShieldUrl: data.type_shield_url || undefined,
        productNumber: data.product_number || undefined,
        serialNumber: data.serial_number || undefined,
        type: data.product_type || undefined,
        dryerTechnology: data.dryer_technology || undefined,
        eprelReference: data.eprel_reference || undefined,
        eprelData: data.eprel_data,
      };

      setAppliance(normalized);
      setIsLoading(false);
      syncToLocalStorage(normalized);
    })();

    return () => {
      cancelled = true;
    };
  }, [id, user, reloadKey]);

  return { appliance, isLoading, notFound, reload };
}
